import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Platform,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { StepHeader, RadioButton, maintenanceStepStyles } from './shared';
import { formatAsPhoneNumber, cleanPhoneNumber } from '@/utils/residents/utils';
import { SVG_COMPONENTS } from '@/assets/admin';

const PhoneSvg = SVG_COMPONENTS.PHONE;

// UI severity options matching Figma design with mappings to Yardi priority levels
// Each option maps to the closest Yardi priority name
const SEVERITY_OPTIONS = [
  {
    id: 'no_affect',
    label: "It doesn't affect me at the moment",
    showPhone: false,
    // Maps to lowest priority: Normal, Low, Very Low (in order of preference)
    priorityKeywords: ['normal', 'low', 'very low'],
    priorityFallbackIndex: -1, // Use last item in list (lowest priority)
  },
  {
    id: 'discomfort',
    label: 'It creates discomfort',
    showPhone: false,
    // Maps to medium priority: Medium, Normal
    priorityKeywords: ['medium', 'normal'],
    priorityFallbackIndex: -2, // Use second-to-last item
  },
  {
    id: 'no_workaround',
    label: 'No reasonable workaround available',
    showPhone: false,
    // Maps to high priority: High, Very High
    priorityKeywords: ['high', 'very high'],
    priorityFallbackIndex: 1, // Use second item in list
  },
  {
    id: 'cant_use',
    label: "Can't use the apartment",
    showPhone: true,
    urgentMessage: 'In case of urgency, please call the maintenance team. Otherwise, continue submitting your request.',
    // Maps to highest priority: Emergency, Urgent, Critical
    priorityKeywords: ['emergency', 'urgent', 'critical'],
    priorityFallbackIndex: 0, // Use first item in list (highest priority)
  },
];

/**
 * Maps a UI severity option ID to the best matching Yardi priority name
 */
const mapSeverityToYardiPriority = (severityId: string, yardiPriorities: string[]): string => {
  if (!yardiPriorities.length) {
    // Fallback if no priorities from API - return a reasonable default
    return 'Normal';
  }

  const option = SEVERITY_OPTIONS.find(opt => opt.id === severityId);
  if (!option) {
    return yardiPriorities[yardiPriorities.length - 1] || 'Normal';
  }

  // Try to find a matching priority by keyword (case-insensitive)
  for (const keyword of option.priorityKeywords) {
    const match = yardiPriorities.find(p => p.toLowerCase().includes(keyword));
    if (match) return match;
  }

  // Fallback to index-based selection
  const index = option.priorityFallbackIndex;
  if (index >= 0) {
    return yardiPriorities[Math.min(index, yardiPriorities.length - 1)];
  } else {
    // Negative index: count from end
    return yardiPriorities[Math.max(0, yardiPriorities.length + index)];
  }
};

/**
 * Reverse maps a Yardi priority name to a UI severity option ID for display
 */
const mapYardiPriorityToSeverity = (priority: string, yardiPriorities: string[]): string => {
  if (!priority) return '';
  
  const lowerPriority = priority.toLowerCase();
  
  // Check each option's keywords to find the best match
  for (const option of [...SEVERITY_OPTIONS].reverse()) {
    for (const keyword of option.priorityKeywords) {
      if (lowerPriority.includes(keyword)) {
        return option.id;
      }
    }
  }
  
  // Fallback: determine by position in priority list
  const index = yardiPriorities.findIndex(p => p === priority);
  if (index === -1) return 'no_affect';
  
  const ratio = index / Math.max(1, yardiPriorities.length - 1);
  if (ratio <= 0.25) return 'cant_use';
  if (ratio <= 0.5) return 'no_workaround';
  if (ratio <= 0.75) return 'discomfort';
  return 'no_affect';
};

interface StepSeverityProps {
  severity: string;
  onSeverityChange: (value: string) => void;
  severityOptions: string[];
  maintenancePhone?: string;
}

/**
 * StepSeverity - Step 2: How this affects you
 * Allows user to select the severity/impact of their issue
 * 
 * The component displays user-friendly severity descriptions but maps them
 * to actual Yardi priority names when submitting.
 */
export const StepSeverity: React.FC<StepSeverityProps> = ({
  severity,
  onSeverityChange,
  severityOptions,
  maintenancePhone,
}) => {
  // Determine which UI option is currently selected by reverse-mapping the Yardi priority
  const selectedSeverityId = useMemo(() => {
    // If severity matches a UI option ID (legacy/direct), use it
    if (SEVERITY_OPTIONS.some(opt => opt.id === severity)) {
      return severity;
    }
    // Otherwise, it's a Yardi priority name - reverse map it
    return mapYardiPriorityToSeverity(severity, severityOptions);
  }, [severity, severityOptions]);

  // Handle selection: map UI option to Yardi priority and call onChange
  const handleSelect = (optionId: string) => {
    const yardiPriority = mapSeverityToYardiPriority(optionId, severityOptions);
    onSeverityChange(yardiPriority);
  };

  const displayOptions = SEVERITY_OPTIONS;

  const handlePhonePress = () => {
    if (!maintenancePhone) return;
    
    const cleaned = cleanPhoneNumber(String(maintenancePhone));
    
    if (Platform.OS === 'ios') {
      const { ActionSheetIOS } = require('react-native');
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ['Call', 'Text', 'Cancel'],
          cancelButtonIndex: 2,
        },
        (buttonIndex: number) => {
          if (buttonIndex === 0) Linking.openURL(`tel:${cleaned}`);
          else if (buttonIndex === 1) Linking.openURL(`sms:${cleaned}`);
        }
      );
    } else {
      Linking.openURL(`tel:${cleaned}`);
    }
  };

  return (
    <View style={maintenanceStepStyles.container}>
      <StepHeader title="How this affects you?" />
      
      <View style={maintenanceStepStyles.content}>
        {displayOptions.map((option) => {
          const isSelected = selectedSeverityId === option.id;
          const showPhoneInfo = isSelected && option.showPhone && maintenancePhone;
          
          return (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.selectionItem,
                showPhoneInfo && styles.selectionItemExpanded,
              ]}
              onPress={() => handleSelect(option.id)}
              activeOpacity={0.7}
            >
              <View style={styles.selectionRow}>
                <Text style={styles.selectionItemText}>{option.label}</Text>
                <RadioButton selected={isSelected} />
              </View>
              
              {/* Show urgent message and phone for "Can't use apartment" option */}
              {showPhoneInfo && option.urgentMessage && (
                <View style={styles.urgentContainer}>
                  <Text style={styles.urgentMessage}>{option.urgentMessage}</Text>
                  <TouchableOpacity
                    style={styles.phoneRow}
                    onPress={handlePhonePress}
                    activeOpacity={0.7}
                  >
                    <View style={styles.phoneIconContainer}>
                      <PhoneSvg width={19} height={19} color={MARKETPLACE_COLORS.accentRed} />
                    </View>
                    <Text style={styles.phoneNumber}>
                      {formatAsPhoneNumber(String(maintenancePhone))}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  selectionItem: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  selectionItemExpanded: {
    paddingVertical: 10,
    gap: 6,
  },
  selectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectionItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  
  // Urgent/Phone section
  urgentContainer: {
    gap: 10,
  },
  urgentMessage: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  phoneIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneNumber: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentRed,
    lineHeight: 21,
  },
});

export default StepSeverity;
